import { Dialog, Transition } from "@headlessui/react";
import { format } from "date-fns";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useCallback, useEffect, useRef, useState, type MouseEvent } from "react";
import { FaWhatsappSquare } from "react-icons/fa";
import { IoLocationOutline } from "react-icons/io5";
import { FiEdit, FiRefreshCw } from "react-icons/fi";
import { setAddressDetail, setAddressSelect } from "@/app_redux/reducers/slices/auth/cart_slice";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { useAddressListAPI } from "@/app/hooks/api_hooks/HDCart/useAddressListAPI";
import { useWhatsappSendRequestAPI } from "@/app/hooks/api_hooks/HDCart/useWhatsappSendRequestAPI";
import { useWhatsappTemplateAPI } from "@/app/hooks/api_hooks/HDCart/useWhatsappTemplateAPI";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import WhatsappTemplatePreviewModal from "@/app/components/ui/WhatsappTemplatePreviewModal";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import OverbookingConfirmationModal from "@/app/components/routes/modals/OverbookingConfirmationModal";
import VMSConstants from "@/constants";
import RBACFlag from "@/app/components/core/RBACFlag";

// Build servers' .env files have shipped without this var, which sent links as "undefined?user_id=...".
const HD_ADDRESS_COLLECTION_URL = import.meta.env.VITE_HD_ADDRESS_URL || "https://address.vetic.in/";
const buildHDAddressCollectionLink = (userId: string) => `${HD_ADDRESS_COLLECTION_URL}?user_id=${userId}&flow=hd_order`;
// At-home bookings only use addresses the customer added themselves (app or address.vetic.in), never staff-added ones.
const AT_HOME_ADDRESS_SOURCES = new Set(["address_request_at_home", "address_request_hd_order"]);
const isCustomerAddedAddress = (address: IAddressListItem) =>
  Boolean(address?.added_by_customer) || AT_HOME_ADDRESS_SOURCES.has(address?.source ?? "");

interface IAddressListItem {
  address_id?: string;
  address_1?: string;
  address_2?: string;
  pincode?: string;
  label?: "Home" | "Office" | "Others" | string;
  previously_ordered?: string | boolean;
  last_used_in_ahs_booking?: boolean;
  created_at?: number;
  source?: string | null;
  added_by_customer?: boolean;
}

interface IAddressListModalProps {
  onClose: () => void;
  isAddressOpen: boolean;
  payloadData?: IAddressListItem[];
  openAddAddress: (addressId?: string) => void;
  onSelectAddress: (address: IAddressListItem) => Promise<void> | void;
  title?: string;
  closeOnOutsideClick?: boolean
  isAtHome?: boolean;
}

const getAddressLabelStyles = (label?: string) => {
  if (label === "Home") return "bg-[#e7f8ef] text-[#07863d]";
  if (label === "Office") return "bg-[#f8e7d6] text-[#a75610]";
  if (label === "Others") return "bg-[#eff1ff] text-[#3969ff]";
  return "bg-[#f2f2f2] text-[#555]";
};

export default function AddressListModal({
  onClose,
  isAddressOpen,
  payloadData,
  openAddAddress,
  onSelectAddress,
  title = "Select Home Delivery Address",
  isAtHome = false,
}: IAddressListModalProps) {
  const requestAddressTemplate = isAtHome ? "request_address_at_home_services" : "request_address_hd_order";
  const cancelButtonRef = useRef(null);
  const dispatch = useAppDispatch();
  const { getRouteKey } = useRouter();
  const { execute: sendWhatsappRequest } = useWhatsappSendRequestAPI();
  const { execute: fetchWhatsappTemplate } = useWhatsappTemplateAPI();
  const { execute: logClickAPI } = useClickLogAPI();
  const fetchAddressList = useAddressListAPI();
  const vmsUserEmail = useAppSelector((state) => state.auth?.user?.email);
  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { customerId, customerDetails } = routeState.state as ICustomerProfileState;
  const canRequestAddress = Boolean(customerDetails?.mobile && customerId);

  const [templateContent, setTemplateContent] = useState<string>("");
  const [showRequestAddressPreviewModal, setShowRequestAddressPreviewModal] = useState(false);
  const [isSendingWhatsapp, setIsSendingWhatsapp] = useState(false);
  const [showRefreshList, setShowRefreshList] = useState(false);
  const [isRefreshingList, setIsRefreshingList] = useState(false);
  const [refreshedAddressList, setRefreshedAddressList] = useState<IAddressListItem[] | null>(null);
  const fullAddressList = refreshedAddressList ?? payloadData;
  const addressListToRender = isAtHome
    ? fullAddressList?.filter(isCustomerAddedAddress)
    : fullAddressList;
  const [isLeaving, setIsLeaving] = useState(false);
  const afterLeaveActionRef = useRef<(() => void | Promise<void>) | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const closeWithTransition = useCallback((afterLeave: () => void | Promise<void>) => {
    afterLeaveActionRef.current = afterLeave;
    setIsLeaving(true);
  }, []);

  const handleAfterLeave = useCallback(async () => {
    const action = afterLeaveActionRef.current;
    afterLeaveActionRef.current = null;
    if (!action) return;
    await action();
    if (isMountedRef.current) setIsLeaving(false);
  }, []);

  const logClick = useCallback(
    (eventType: string, buttonName: string, extras?: Partial<IClickLogBody>) => {
      Promise.resolve().then(() => {
        logClickAPI({
          event_type: eventType,
          scr_id: "HD_ADDRESS_LIST_MODAL",
          user_id: customerId,
          phone: customerDetails?.mobile,
          vms_user_id: vmsUserEmail ?? "",
          cta_text: buttonName,
          timestamp: Date.now(),
          ...extras,
        });
      });
    },
    [logClickAPI, customerId, customerDetails?.mobile, vmsUserEmail],
  );

  const handleWhatsappRequest = useCallback(async () => {
    if (!canRequestAddress) {
      logClick("REQUEST_ADDRESS_CLICK", "Request Address");
      return;
    }

    const response = await fetchWhatsappTemplate({
      template_name: requestAddressTemplate,
      user_id: customerId,
    });

    if (response?.status) {
      setTemplateContent(response.data.template_content);
      setShowRequestAddressPreviewModal(true);
    }
    logClick("REQUEST_ADDRESS_CLICK", "Request Address");
  }, [canRequestAddress, customerId, fetchWhatsappTemplate, logClick, requestAddressTemplate]);

  const sendWhatsappRequestConfirm = useCallback(
    async (mobile: string) => {
      if (!customerId || !mobile) {
        logClick("SEND_WHATSAPP_CLICK", "Send WhatsApp", { phone: mobile });
        return;
      }
      setIsSendingWhatsapp(true);
      const response = await sendWhatsappRequest({
        send_to: mobile,
        template_name: requestAddressTemplate,
        variables: {
          // AHS template already embeds the address URL ending in "?user_id="; pass only the user_id (plus the
          // flow tag address.vetic.in records as the address source), else the link doubles up / breaks.
          1: isAtHome ? `${customerId}&flow=at_home` : buildHDAddressCollectionLink(customerId),
        },
      });
      setIsSendingWhatsapp(false);
      if (response?.status) {
        setShowRequestAddressPreviewModal(false);
        setShowRequestAddressSuccessModal(true);
        setShowRefreshList(true);
      }
      logClick("SEND_WHATSAPP_CLICK", "Send WhatsApp", { phone: mobile });
    },
    [customerId, sendWhatsappRequest, logClick, requestAddressTemplate],
  );

  const handleRefreshList = useCallback(async () => {
    if (!customerId || isRefreshingList) return;
    setIsRefreshingList(true);
    try {
      const addresses = await fetchAddressList(customerId, null, null, mappedClinicId, true);
      // On API failure `addresses` is null — keep the previously shown list instead of wiping it.
      if (addresses) {
        setRefreshedAddressList(addresses);
      }
    } finally {
      setIsRefreshingList(false);
    }
    logClick("REFRESH_ADDRESS_LIST_CLICK", "Refresh List");
  }, [customerId, isRefreshingList, fetchAddressList, mappedClinicId, logClick]);

  const handleSelectAddress = useCallback(
    (address: IAddressListItem) => {
      logClick("SELECT_ADDRESS_CLICK", "Select Address", { address_id: address?.address_id });
      closeWithTransition(() => onSelectAddress(address));
    },
    [closeWithTransition, logClick, onSelectAddress],
  );

  const handleCopyScriptLog = useCallback(() => {
    logClick("COPY_SCRIPT_CLICK", "Copy Script", { scr_id: "HD_WHATSAPP_PREVIEW_MODAL" });
  }, [logClick]);

  const handleMobileChangeLog = useCallback(
    (mobile: string) => {
      logClick("MOBILE_NUMBER_CHANGE", "Mobile Number", { scr_id: "HD_WHATSAPP_PREVIEW_MODAL", phone: mobile });
    },
    [logClick],
  );

  const handlePreviewCancelLog = useCallback(() => {
    logClick("WHATSAPP_PREVIEW_CANCEL_CLICK", "Cancel", { scr_id: "HD_WHATSAPP_PREVIEW_MODAL" });
  }, [logClick]);

  const handleClose = useCallback(() => {
    closeWithTransition(onClose);
  }, [closeWithTransition, onClose]);

  const handleAddAddress = useCallback(() => {
    dispatch(setAddressDetail({ data: null }));
    dispatch(setAddressSelect({ data: null }));
    closeWithTransition(() => {
      onClose();
      openAddAddress();
    });
  }, [closeWithTransition, dispatch, onClose, openAddAddress]);

  const handleEditAddress = useCallback(
    (event: MouseEvent<HTMLButtonElement>, addressId?: string) => {
      event.stopPropagation();
      closeWithTransition(() => {
        onClose();
        openAddAddress(addressId);
      });
    },
    [closeWithTransition, onClose, openAddAddress],
  );
  const [showRequestAddressSuccessModal, setShowRequestAddressSuccessModal] = useState(false);

  return (
    <div>
      <Transition.Root
        appear
        show={isAddressOpen && !showRequestAddressPreviewModal && !isLeaving}
        as={Fragment}
        afterLeave={handleAfterLeave}
      >
        <Dialog as="div" className="relative z-40" initialFocus={cancelButtonRef} onClose={() => { }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  className={`relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-4 sm:max-w-2xl sm:w-full sm:p-6 h-[50vh] flex flex-col `}
                >
                  <div className="flex justify-between items-center border-b pb-2">
                    <Dialog.Title className="font-semibold text-sm">{title}</Dialog.Title>
                    <div className="flex items-center gap-3">
                      {showRefreshList && (
                        <button
                          type="button"
                          disabled={isRefreshingList}
                          className={`flex gap-2 items-center rounded-md p-2 bg-[#f5f5f5] ${isRefreshingList ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                            }`}
                          onClick={handleRefreshList}
                        >
                          <FiRefreshCw color="#0068ff" size={18} className={isRefreshingList ? "animate-spin" : ""} />
                          <span className="text-[rgba(0,104,255,1)] text-sm font-semibold">
                            {isRefreshingList ? "Refreshing..." : "Refresh List"}
                          </span>
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={!canRequestAddress}
                        className={`flex gap-2 items-center rounded-md p-2 ${canRequestAddress ? "bg-[#f5f5f5] cursor-pointer" : "bg-[#f7f7f7] cursor-not-allowed opacity-60"
                          }`}
                        onClick={handleWhatsappRequest}
                      >
                        <FaWhatsappSquare color="green" size={22} />
                        <span className="text-[rgba(0,104,255,1)] text-sm font-semibold">Request Address</span>
                      </button>
                      <button
                        type="button"
                        className="rounded-md p-1 text-[#6a717e] hover:bg-[#f2f2f2] hover:text-[#1f2937] focus:outline-none focus:ring-2 focus:ring-[#0068ff]"
                        onClick={handleClose}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3 overflow-y-auto pr-2 flex-1">
                    <RBACFlag actionId="HD_ADDRESS_ADD_NEW_RESTRICTED">
                      <div
                        className="border-2 w-full h-auto border-dashed p-8 bg-[rgba(245,245,245,1)] flex flex-col items-center justify-center text-center rounded-md cursor-pointer"
                        onClick={handleAddAddress}
                      >
                        <div className="bg-white p-2 rounded-md w-10 flex items-center justify-center text-[rgba(108,114,127,1)]">
                          <IoLocationOutline size={22} />
                        </div>
                        <div className="text-xs font-medium mt-2">Add New Address</div>
                      </div>
                    </RBACFlag>
                    {addressListToRender?.map((address, index) => (
                      <div
                        key={address?.address_id ?? `${address?.address_1 ?? "address"}-${index}`}
                        className="border-2 p-2 w-full rounded-md h-auto cursor-pointer"
                        onClick={() => handleSelectAddress(address)}
                      >
                        <div className="flex items-center gap-2 justify-between">
                          <div className="flex gap-2 items-center">
                            <div className="font-medium text-sm">Delivery Address</div>
                            <div className={`rounded-2xl py-1 px-3 text-[10px] font-medium ${getAddressLabelStyles(address?.label)}`}>
                              {address?.label}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="cursor-pointer"
                            onClick={(event) => handleEditAddress(event, address?.address_id)}
                          >
                            <FiEdit size={22} />
                          </button>
                        </div>
                        <div className="text-[#6a717e] text-xs mt-3">{address?.address_1}</div>
                        <div className="text-[#6a717e] text-xs mt-1">{address?.address_2}</div>
                        <div className="flex items-end justify-between gap-2">
                          <div>
                            {Boolean(address?.previously_ordered) && (
                              <div className="rounded-md bg-[#dedede] text-[#666666] w-24 p-1 text-[10px] text-center mt-3">
                                Last Delivered
                              </div>
                            )}
                            {Boolean(address?.last_used_in_ahs_booking) && (
                              <div className="rounded-md bg-[#e7f8ef] text-[#07863d] w-fit px-2 p-1 text-[10px] text-center mt-3">
                                Last used in AHS booking
                              </div>
                            )}
                          </div>
                          {Boolean(address?.created_at) && (
                            <div className="text-[#6a717e] text-[10px] font-semibold mt-3 ml-auto whitespace-nowrap">
                              Created At {format(new Date(address.created_at * 1000), "dd MMM yyyy")}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <WhatsappTemplatePreviewModal
        open={showRequestAddressPreviewModal}
        onClose={() => setShowRequestAddressPreviewModal(false)}
        initialMobile={customerDetails?.mobile ?? ""}
        script={templateContent}
        isSending={isSendingWhatsapp}
        onSend={sendWhatsappRequestConfirm}
        onCancel={handlePreviewCancelLog}
        onCopyScript={handleCopyScriptLog}
        onMobileChange={handleMobileChangeLog}
      />
      <OverbookingConfirmationModal
        open={showRequestAddressSuccessModal}
        title="Address requested on WhatsApp"
        message={VMSConstants.string.whatsappAddressRequestSuccessMessage}
        cancelText="OK"
        onClose={() => setShowRequestAddressSuccessModal(false)}
        onConfirm={() => setShowRequestAddressSuccessModal(false)}
        confirmText=""
        confirmButtonClassName="hidden"
      />
      {/* {showClusterChangeModal && (
        <AddressClusterChangeModal isOpen={showClusterChangeModal} onClose={handleCloseClusterChangeModal} />
      )} */}
    </div>
  );
}
